import axios from 'axios';
import { acquireToken } from './authService';
import msalInstance from './authService';

const api = axios.create({
    baseURL: process.env.REACT_APP_BACKEND_URL || "https://localhost:44349",
    headers: {
      'Content-Type': 'application/json'
    }
  });

// Update baseURL after runtime config loads
const updateBaseURL = async () => {
  try {
    const response = await fetch('/.env');
    const envContent = await response.text();
    
    for (const line of envContent.split('\n')) {
      if (line.startsWith('REACT_APP_BACKEND_URL=')) {
        const value = line.split('=')[1]?.trim();
        if (value) {
          api.defaults.baseURL = value;
          break;
        }
      }
    }
  } catch (error) {
    console.warn('Could not load runtime .env file:', error);
  }
};

// Call this when your app initializes
updateBaseURL();
  
  // Track if we're already redirecting to prevent loops
  let isRedirecting = false;

  // Helper function to handle logout with proper cleanup
  const handleSessionExpired = async () => {
    // Only logout if not already redirecting and not on login page
    if (isRedirecting || window.location.pathname === '/login') {
      return;
    }

    console.warn('Session expired, redirecting to login');
    isRedirecting = true;

    try {
      // Clear MSAL active account
      msalInstance.setActiveAccount(null);

      // Get the active account for logout
      const account = msalInstance.getAllAccounts()[0];

      // Clear storage
      sessionStorage.clear();
      localStorage.clear();

      // Perform MSAL logout with redirect
      if (account) {
        await msalInstance.logoutRedirect({
          account: account,
          postLogoutRedirectUri: window.location.origin + '/login'
        });
      } else {
        // No account found, just redirect to login
        window.location.href = '/login';
      }
    } catch (error) {
      console.error('Error during logout:', error);
      // Fallback: just redirect to login
      window.location.href = '/login';
    }
  };

  // Add request interceptor to include auth token
  api.interceptors.request.use(async (config) => {
    try {
      const token = await acquireToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    } catch (error: any) {
      console.error('Error getting token:', error);

      // If token acquisition fails due to expiration, trigger logout
      if (error?.message?.includes('Token expired') ||
          error?.message?.includes('re-authentication required') ||
          error?.message?.includes('No active account')) {

        // Trigger session expired handler
        handleSessionExpired();

        // Don't make the API call if token is expired
        return Promise.reject(new Error('Authentication required'));
      }

      return Promise.reject(error);
    }
  });

  // Add response interceptor for error handling
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response) {
        // Handle specific error status codes
        switch (error.response.status) {
          case 401:
            // Unauthorized - trigger session expired handler
            console.error('API returned 401 Unauthorized, session expired');
            await handleSessionExpired();
            break;
          case 403:
            // Forbidden
            console.error('Access forbidden:', error.response.data);
            break;
          default:
            console.error('API Error:', error.response.data);
        }
      } else if (error.request) {
        // Request made but no response received
        console.error('No response received:', error.request);
      } else {
        // Error setting up request
        console.error('Request error:', error.message);
      }
      return Promise.reject(error);
    }
  );
  
  export default api;